import { FileSignature } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './css/AluminiumDeals.css';
import { ALUMINIUM_SECTIONS } from './aluminium-deals/constants';
import DiscountSection from './aluminium-deals/DiscountSection';
import MontageSection from './aluminium-deals/MontageSection';
import TranslationSection from './aluminium-deals/TranslationSection';

const SECTIONS = [
  { key: 'discount', Component: DiscountSection, allowed: permissions => permissions.discount },
  { key: 'montage', Component: MontageSection, allowed: permissions => permissions.montage },
  { key: 'translation', Component: TranslationSection, allowed: permissions => permissions.translation },
];

const SECTION_DESCRIPTIONS = {
  discount: 'Automatyzacja rabatowania ofert aluminium',
  montage: 'Pół-automatyczne naliczanie montaży na ofertach aluminium',
  translation: 'Automatyczne tłumaczenie ofert aluminium',
};

// Powłoka strony: nagłówek, nawigacja między modułami, uprawnienia i podgląd instrukcji.
// Każda sekcja ma własny stan, więc zmiana w jednej nie renderuje pozostałych.
const AluminiumDeals = ({ section = 'discount' }) => {
  const { hasPermission } = useAuth();
  const permissions = {
    discount: hasPermission('aluminium_deals:use'),
    montage: hasPermission('aluminium_deals:montage'),
    translation: hasPermission('aluminium_deals:translation'),
  };
  const [activeInstruction, setActiveInstruction] = useState(null);
  const [visited, setVisited] = useState(() => new Set([section]));

  useEffect(() => {
    setVisited(previous => (previous.has(section) ? previous : new Set(previous).add(section)));
  }, [section]);

  useEffect(() => {
    if (!activeInstruction) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setActiveInstruction(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [activeInstruction]);

  const visibleSections = ALUMINIUM_SECTIONS.filter(item => (
    !item.permission || hasPermission(item.permission)
  ));

  return <div className="page-container aluminium-page">
    <div className="page-header aluminium-page-header">
      <div><h2 className="sales-module-title"><FileSignature aria-hidden="true" /> Oferty aluminium</h2><p>{SECTION_DESCRIPTIONS[section]}</p></div>
    </div>
    <nav className="aluminium-section-nav" aria-label="Moduły umów aluminium">
      {visibleSections.map(item => {
        const Icon = item.icon;
        return <NavLink key={item.key} to={item.path} className={({ isActive }) => `aluminium-section-link ${isActive ? 'active' : ''}`}>
          <Icon size={19} aria-hidden="true" />
          <span>{item.label}</span>
        </NavLink>;
      })}
    </nav>
    <div className="page-content">
      {SECTIONS.filter(({ key, allowed }) => allowed(permissions) && (key === section || visited.has(key))).map(({ key, Component }) => (
        // Odwiedzone sekcje zostają zamontowane (ukryte), dzięki czemu wybrany plik i ustawienia
        // nie giną po przełączeniu zakładki — tak jak przy jednym komponencie współdzielonym przez trasy.
        <div key={key} style={{ display: key === section ? 'contents' : 'none' }}>
          <Component onOpenInstruction={setActiveInstruction} />
        </div>
      ))}
    </div>

    {activeInstruction && <div className="aluminium-instruction-overlay" role="presentation" onMouseDown={() => setActiveInstruction(null)}>
      <section className="aluminium-instruction-modal" role="dialog" aria-modal="true" aria-labelledby="aluminium-instruction-title" onMouseDown={event => event.stopPropagation()}>
        <header className="aluminium-instruction-modal-header">
          <h3 id="aluminium-instruction-title">{activeInstruction.title}</h3>
          <button type="button" className="aluminium-instruction-close" onClick={() => setActiveInstruction(null)} aria-label="Zamknij podgląd instrukcji">×</button>
        </header>
        <iframe className="aluminium-instruction-frame" title={`${activeInstruction.title} PDF`} src={activeInstruction.url} />
        <footer className="aluminium-instruction-modal-footer">
          <a className="aluminium-instruction-download" href={activeInstruction.url} download={activeInstruction.filename}>⬇ Pobierz PDF</a>
        </footer>
      </section>
    </div>}
  </div>;
};

export default AluminiumDeals;
